import { isWordDocumentMime, resolveStoredDocumentMimeType } from '@/utils/fileMime';

export type PreviewKind = 'pdf' | 'word' | 'image' | 'video' | 'text' | 'csv' | 'sheet' | 'none';

const SHEET_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroenabled.12',
]);

const CSV_MIME_TYPES = new Set(['text/csv', 'application/csv', 'text/tab-separated-values']);

const TEXT_MIME_TYPES = new Set(['text/plain', 'text/markdown']);

const DISPLAYABLE_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/svg+xml',
  'image/avif',
  'image/apng',
  'image/x-icon',
]);

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm', 'avi']);
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif']);

function extensionOf(filename: string | null | undefined): string {
  const base = (filename ?? '').split(/[\\/]/).pop() ?? '';
  return base.includes('.') ? (base.split('.').pop()?.toLowerCase() ?? '') : '';
}

export function classifyPreview(
  mimeType: string | null | undefined,
  filename?: string | null,
): PreviewKind {
  const type = resolveStoredDocumentMimeType({ mimeType, filename });
  const ext = extensionOf(filename);

  if (type === 'application/pdf') return 'pdf';
  if (isWordDocumentMime(type) || ext === 'doc' || ext === 'docx') return 'word';
  if (SHEET_MIME_TYPES.has(type) || ext === 'xlsx' || ext === 'xlsm') return 'sheet';
  if (CSV_MIME_TYPES.has(type) || ext === 'csv' || ext === 'tsv') return 'csv';
  if (TEXT_MIME_TYPES.has(type) || ext === 'txt' || ext === 'log' || ext === 'md') return 'text';
  if (DISPLAYABLE_IMAGE_TYPES.has(type) || IMAGE_EXTENSIONS.has(ext)) return 'image';
  if (type.startsWith('image/')) return 'none';
  if (type.startsWith('video/') || VIDEO_EXTENSIONS.has(ext)) return 'video';
  return 'none';
}

/** i18n key for the header subtitle. `none` keeps the raw MIME type. */
export function previewKindLabelKey(kind: PreviewKind): string | null {
  switch (kind) {
    case 'pdf':
      return 'documents.highlights.fileKindPdf';
    case 'word':
      return 'documents.highlights.fileKindWord';
    case 'text':
      return 'documents.preview.kind_text';
    case 'csv':
      return 'documents.preview.kind_csv';
    case 'sheet':
      return 'documents.preview.kind_sheet';
    case 'image':
      return 'documents.preview.kind_image';
    case 'video':
      return 'documents.preview.kind_video';
    case 'none':
      return null;
    default: {
      const exhaustive: never = kind;
      return exhaustive;
    }
  }
}
